import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { AlertCircle, Trash2 } from 'lucide-react';

const DeleteAccount = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('You must be logged in to delete your account');
        setIsDeleting(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Clear all user data from localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirect to home page
        alert('Your account has been permanently deleted.');
        navigate('/');
        window.location.reload();
      } else {
        setError(data.message || 'Failed to delete account. Please try again.');
        setIsDeleting(false);
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('An error occurred. Please try again later.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-900">Delete Your Account</h1>
          </div>

          {/* Warning Banner */}
          <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  This action cannot be undone
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Once you delete your account, all your data will be permanently removed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                What happens when you delete your account?
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Your profile and personal information will be permanently deleted</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>All your AI credits and subscriptions will be cancelled</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Your learning progress and saved content will be lost</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>You will be logged out from all devices</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Your connections with friends will be removed</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>This action is permanent and cannot be reversed</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Before you go...
              </h2>
              <p className="text-gray-700 mb-2">
                If you're experiencing issues with your account, please consider:
              </p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li>• Contacting our support team at <a href="mailto:support@nerdindians.com" className="text-blue-600 hover:underline">support@nerdindians.com</a></li>
                <li>• Taking a break instead of permanently deleting</li>
                <li>• Reviewing our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a></li>
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Delete Button */}
            <div className="pt-6 border-t">
              <Button
                onClick={() => setShowConfirmDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 flex items-center gap-2"
                disabled={isDeleting}
              >
                <Trash2 className="h-5 w-5" />
                Delete My Account Permanently
              </Button>
            </div>
          </div>
        </div>

        {/* Back to Dashboard Link */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:underline"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <h3 className="text-xl font-bold text-gray-900">Confirm Account Deletion</h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you absolutely sure you want to delete your account? This action is permanent and cannot be undone. All your data will be lost forever.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete Forever'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
